import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { DateRange } from '../components/common/DateRangePicker';

export interface NutritionDaySummary {
  date: string;
  calories: number;
  fats_g: number;
  saturated_fat_g: number;
  polyunsaturated_fat_g: number;
  monounsaturated_fat_g: number;
  carbs_g: number;
  fiber_g: number;
  sugar_g: number;
  protein_g: number;
  cholesterol_mg: number;
  sodium_mg: number;
  potassium_mg: number;
  meals_logged: number;
  foods_count: number;
}

export interface NutritionSummary {
  days: NutritionDaySummary[];
  totals: {
    calories: number;
    protein_g: number;
    carbs_g: number;
    fats_g: number;
    days_with_data: number;
  };
  averages: {
    calories: number;
    protein_g: number;
    carbs_g: number;
    fats_g: number;
  };
}

export interface ConnectionStatus {
  connected: boolean;
  last_sync: string | null;
  user_email: string | null;
}

interface UseNutritionSummaryResult {
  summary: NutritionSummary | null;
  connectionStatus: ConnectionStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  triggerSync: () => Promise<any>;
}

/**
 * Hook to fetch nutrition data from FatSecret integration
 */
export function useNutritionSummary(
  clientId: string,
  dateRange: DateRange
): UseNutritionSummaryResult {
  const [summary, setSummary] = useState<NutritionSummary | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const autoSynced = useRef(false);

  const loadData = useCallback(async () => {
    if (!clientId || !dateRange.startDate || !dateRange.endDate) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch connection status and summary in parallel
      const [statusRes, summaryRes] = await Promise.all([
        fetch(`/api/nutrition/${clientId}/connection`, { headers }),
        fetch(
          `/api/nutrition/${clientId}/summary?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
          { headers }
        )
      ]);

      // Handle connection status
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setConnectionStatus({
          connected: statusData.connected || false,
          last_sync: statusData.lastSync || statusData.last_sync || null,
          user_email: statusData.userEmail || statusData.user_email || null
        });
      } else {
        setConnectionStatus({ connected: false, last_sync: null, user_email: null });
      }

      // Handle summary data
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();

        // Calculate totals and averages from days
        // API returns { days: count, summaries: array } - we need the summaries array
        const days = summaryData.summaries || [];
        const daysWithData = days.filter((d: NutritionDaySummary) => d.calories > 0);

        const totals = daysWithData.reduce(
          (acc: any, day: NutritionDaySummary) => ({
            calories: acc.calories + day.calories,
            protein_g: acc.protein_g + day.protein_g,
            carbs_g: acc.carbs_g + day.carbs_g,
            fats_g: acc.fats_g + day.fats_g,
            days_with_data: acc.days_with_data + 1
          }),
          { calories: 0, protein_g: 0, carbs_g: 0, fats_g: 0, days_with_data: 0 }
        );

        const dayCount = daysWithData.length || 1;
        const averages = {
          calories: Math.round(totals.calories / dayCount),
          protein_g: Math.round(totals.protein_g / dayCount),
          carbs_g: Math.round(totals.carbs_g / dayCount),
          fats_g: Math.round(totals.fats_g / dayCount)
        };

        setSummary({
          days,
          totals,
          averages
        });
      } else if (summaryRes.status === 404) {
        // No data available - not an error
        setSummary({
          days: [],
          totals: { calories: 0, protein_g: 0, carbs_g: 0, fats_g: 0, days_with_data: 0 },
          averages: { calories: 0, protein_g: 0, carbs_g: 0, fats_g: 0 }
        });
      } else {
        throw new Error('Failed to fetch nutrition summary');
      }
    } catch (err) {
      console.error('Error loading nutrition data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load nutrition data');
    } finally {
      setLoading(false);
    }
  }, [clientId, dateRange.startDate, dateRange.endDate]);

  const triggerSync = useCallback(async (): Promise<any> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return false;

      // Manual sync: force sync for the currently displayed date range
      // Cap endDate at today — don't sync future dates
      const startDate = dateRange.startDate;
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const endDate = dateRange.endDate > todayStr ? todayStr : dateRange.endDate;

      const response = await fetch(`/api/nutrition/${clientId}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ startDate, endDate, forceSync: true })
      });

      if (response.ok) {
        const result = await response.json();
        // Reload data after sync
        await loadData();
        return result;
      }
      throw new Error('Sync failed');
    } catch (err) {
      console.error('Error triggering sync:', err);
      throw err;
    }
  }, [clientId, dateRange.startDate, dateRange.endDate, loadData]);

  // Auto-sync: check staleness after initial load
  const autoSyncIfStale = useCallback(async (status: ConnectionStatus | null) => {
    if (autoSynced.current || !status?.connected) return;
    autoSynced.current = true;

    // Skip if we already auto-synced this client this session
    const sessionKey = `nutrition_auto_synced_${clientId}`;
    const lastAutoSync = sessionStorage.getItem(sessionKey);
    if (lastAutoSync) {
      const elapsed = Date.now() - Number(lastAutoSync);
      if (elapsed < 4 * 60 * 60 * 1000) return;
    }

    // Stamp immediately to prevent race conditions (React StrictMode double-mount)
    sessionStorage.setItem(sessionKey, String(Date.now()));

    const toastId = toast.loading('Syncing nutrition data from FatSecret...');
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { toast.dismiss(toastId); return; }

      const response = await fetch(`/api/nutrition/${clientId}/smart-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const syncedDays = result.syncResult?.syncedDays || 0;
        if (syncedDays > 0) {
          toast.success(`Found ${syncedDays} new day${syncedDays > 1 ? 's' : ''} of nutrition data`, { id: toastId });
          await loadData();
        } else {
          toast.dismiss(toastId);
        }
      } else {
        toast.error('FatSecret sync failed', { id: toastId });
      }
    } catch {
      toast.error('FatSecret sync failed', { id: toastId });
    }
  }, [clientId, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Trigger auto-sync after connection status is known
  useEffect(() => {
    if (connectionStatus && !loading) {
      autoSyncIfStale(connectionStatus);
    }
  }, [connectionStatus, loading, autoSyncIfStale]);

  return {
    summary,
    connectionStatus,
    loading,
    error,
    refresh: loadData,
    triggerSync
  };
}

/**
 * Fetch day detail (entries) for a specific date
 * Returns food entries grouped by meal type
 */
export async function fetchDayDetail(
  clientId: string,
  date: string
): Promise<{ entries: FoodEntry[]; error: string | null }> {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return { entries: [], error: 'No authentication token found' };
    }

    const response = await fetch(`/api/nutrition/${clientId}/day/${date}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      return { entries: data.entries || [], error: null };
    } else if (response.status === 404) {
      return { entries: [], error: null };
    } else {
      return { entries: [], error: 'Failed to fetch day detail' };
    }
  } catch (err) {
    console.error('Error fetching day detail:', err);
    return {
      entries: [],
      error: err instanceof Error ? err.message : 'Failed to fetch day detail'
    };
  }
}

export interface FoodEntry {
  id: string;
  food_name: string;
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snacks' | 'other';
  serving_size: string | null;
  number_of_servings: number;
  calories: number;
  fats_g: number;
  saturated_fat_g: number;
  polyunsaturated_fat_g: number;
  monounsaturated_fat_g: number;
  carbs_g: number;
  fiber_g: number;
  sugar_g: number;
  protein_g: number;
  cholesterol_mg: number;
  sodium_mg: number;
  potassium_mg: number;
}

export default useNutritionSummary;
